import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  Youtube,
  Zap,
  Target,
  AlignLeft,
  Users,
  Settings,
  Sparkles,
  CheckCircle,
  FileText,
  Clock,
  PlayCircle,
  MessageCircle,
  TrendingUp,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../firebase.init";
import { validateGeminiKey } from "../../../../utils/geminiUtils";
import { deductUserCredit } from "../../../../utils/geminiUtils";

const YoutubeScriptGenerator = ({ toolPath = "/user/script-outline" }) => {
  // State management
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("General Audience");
  const [wordLength, setWordLength] = useState("500 words");
  const [tone, setTone] = useState("Casual");
  const [goal, setGoal] = useState("Growth");
  const [includeCta, setIncludeCta] = useState(true);
  const [includeHooks, setIncludeHooks] = useState(true);

  // Generated content section (Structured JSON)
  const [scriptData, setScriptData] = useState(null);

  // Options
  const lengthOptions = [
    "100 words",
    "250 words",
    "500 words",
    "1000 words",
    "1000+ words",
  ];
  const toneOptions = [
    "Professional",
    "Casual",
    "Energetic",
    "Educational",
    "Storytelling",
  ];
  const goalOptions = ["Growth", "Engagement", "Sales", "Authority"];
  const audienceOptions = [
    "General Audience",
    "Beginners",
    "Experts",
    "Professionals",
    "Gamers",
    "Students",
  ];

  // Initial Data Fetch
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        return;
      }
      setLoading(false);
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering for Teleprompter-Ready Script
  const constructPrompt = () => {
    return `You are an elite YouTube scriptwriter. Your task is to write a highly engaging, teleprompter-ready script outline based on the user's parameters.
    
    PARAMETERS:
    - Video Topic: ${topic}
    - Target Audience: ${audience}
    - Approximate Length: ${wordLength}
    - Tone: ${tone}
    - Video Goal: ${goal}
    - Include Call-to-Action: ${includeCta ? "Yes" : "No"}
    - Generate Hook Variations: ${includeHooks ? "Yes (Generate 2-3 variations)" : "No (Generate exactly 1 solid hook)"}
    
    WRITING STYLE RULES:
    - Write in a spoken, human-like, conversational style. Avoid robotic or overly formal language.
    - Keep sentences short, punchy, and natural to read aloud.
    - Use storytelling elements where appropriate.
    - Optimize for viewer retention by opening "loops" (teasing what's coming next).
    
    CRITICAL INSTRUCTION:
    Respond ONLY with a raw, valid JSON object. Do not include markdown blocks like \`\`\`json.
    
    JSON SCHEMA REQUIRED:
    {
      "title": "A catchy suggested title for the script",
      "hooks": ["An attention-grabbing opener. Start directly with the problem or curiosity."],
      "intro": "Set expectations. Introduce the topic and explain exactly what the viewer will learn.",
      "sections": [
        {
          "heading": "Clear Section Heading (e.g., [01:30] The Core Problem)",
          "script": "The actual spoken script for this section. Smooth transitions. Short paragraphs."
        }
      ],
      "engagement": "A natural question or engagement prompt to ask the audience to comment/like.",
      "cta": "${includeCta ? "The specific Call to Action (subscribe, watch next, or sales pitch if Goal is Sales)." : ""}",
      "outro": "Summary, reinforcement of value, and a clear closing line."
    }`;
  };

  // Generate content
  const generateScript = async () => {
    if (!topic.trim() || !audience.trim()) {
      setError("Please provide both a Video Topic and Target Audience.");
      return;
    }

    setGenerating(true);
    setError("");
    setScriptData(null);

    try {
      // Validate user Gemini key
      const key = await validateGeminiKey(user.email);
      if (!key) {
        setGenerating(false);
        return;
      }

      // Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${key}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.85,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 3000,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (
        data.candidates &&
        data.candidates.length > 0 &&
        data.candidates[0].content
      ) {
        let generatedText = data.candidates[0].content.parts[0].text;

        // Clean markdown backticks to parse JSON safely
        generatedText = generatedText
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();

        try {
          const parsedContent = JSON.parse(generatedText);
          setScriptData(parsedContent);
          toast.success("YouTube script generated successfully!");
        } catch (parseErr) {
          throw new Error("AI returned invalid data format. Please try again.");
        }
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      try {
        await deductUserCredit(user.email, -1, toolPath);
      } catch (e) {}
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const getFormattedScript = () => {
    if (!scriptData) return "";
    let text = `TITLE: ${scriptData.title}\n\n`;

    text += `=== HOOK(S) ===\n`;
    scriptData.hooks.forEach(
      (hook, i) => (text += `Option ${i + 1}: ${hook}\n\n`),
    );

    text += `=== INTRO ===\n${scriptData.intro}\n\n`;

    text += `=== MAIN CONTENT ===\n`;
    scriptData.sections.forEach((sec) => {
      text += `[${sec.heading}]\n${sec.script}\n\n`;
    });

    if (scriptData.engagement) {
      text += `=== ENGAGEMENT PROMPT ===\n${scriptData.engagement}\n\n`;
    }

    if (scriptData.cta) {
      text += `=== CALL TO ACTION ===\n${scriptData.cta}\n\n`;
    }

    text += `=== OUTRO ===\n${scriptData.outro}\n`;

    return text;
  };

  const copyToClipboard = () => {
    const text = getFormattedScript();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`Full script copied to clipboard!`);
      })
      .catch(() => {
        toast.error(`Failed to copy script`);
      });
  };

  const downloadContent = () => {
    const text = getFormattedScript();
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${topic.replace(/\s+/g, "-")}-script.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Script downloaded!");
  };

  const resetForm = () => {
    setTopic("");
    setAudience("General Audience");
    setWordLength("500 words");
    setTone("Casual");
    setGoal("Growth");
    setIncludeCta(true);
    setIncludeHooks(true);
    setScriptData(null);
    setError("");
  };

  if (loadingAuth || loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f1f5f9",
              borderTop: "4px solid #ef4444",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 10px",
            }}
          ></div>
          <p style={{ color: "#64748b", fontWeight: "500" }}>
            Loading Workspace...
          </p>
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "3rem",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <Youtube size={48} color="#ef4444" style={{ marginBottom: "1rem" }} />
          <h2
            style={{
              color: "#0f172a",
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
              fontWeight: "700",
            }}
          >
            Authentication Required
          </h2>
          <button
            onClick={() => (window.location.href = "/login")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              width: "100%",
            }}
          >
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .script-container { font-family: 'Inter', system-ui, -apple-system, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.03); border: 1px solid #e2e8f0; padding: 1.75rem; transition: all 0.3s ease; }
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1); background: #fff; }
        textarea.form-control { resize: vertical; min-height: 80px; }
        
        .gradient-btn { background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2); }
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(239, 68, 68, 0.3); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        .outline-btn { background: transparent; border: 1px solid #cbd5e1; color: #64748b; padding: 0.875rem 1.5rem; border-radius: 10px; font-weight: 600; cursor: pointer; transition: all 0.2s; width: 100%; }
        .outline-btn:hover { background: #f1f5f9; color: #0f172a; }
        
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #10b981; }
        input:checked + .slider:before { transform: translateX(20px); }
        
        .script-block { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.25rem; }
        .script-badge { background: #e2e8f0; color: #475569; padding: 0.3em 0.8em; border-radius: 20px; font-weight: 700; font-size: 0.75rem; display: inline-flex; align-items: center; gap: 6px; margin-bottom: 1rem; letter-spacing: 0.05em; text-transform: uppercase; }
        .script-text { font-family: 'Georgia', serif; font-size: 1.1rem; line-height: 1.8; color: #1e293b; margin: 0; }
        
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .animate-pulse-custom { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="script-container">
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Top Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "2.5rem",
            }}
          >
            <button
              onClick={() => window.history.back()}
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                padding: "8px 16px",
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: "600",
                color: "#64748b",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              ← Dashboard
            </button>
          </div>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "800",
                margin: "0",
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                letterSpacing: "-0.02em",
              }}
            >
              <Youtube color="#ef4444" size={40} /> YouTube Script Outline
              Generator
            </h1>
            <p
              style={{
                color: "#64748b",
                marginTop: "12px",
                fontSize: "1.1rem",
                maxWidth: "600px",
                margin: "12px auto 0",
              }}
            >
              Generate structured, teleprompter-ready video scripts optimized
              for viewer retention and engagement.
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "#fef2f2",
                color: "#ef4444",
                padding: "1rem",
                borderRadius: "10px",
                marginBottom: "2rem",
                textAlign: "center",
                border: "1px solid #fecaca",
                fontWeight: "600",
              }}
            >
              {error}
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "2rem",
              margin: 0,
            }}
          >
            {/* Left Column - Input Form */}
            <div style={{ flex: "1 1 380px", maxWidth: "480px" }}>
              <div className="saas-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "1.5rem",
                    paddingBottom: "1rem",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Target size={20} color="#ef4444" />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.25rem",
                      color: "#0f172a",
                      fontWeight: "700",
                    }}
                  >
                    Script Parameters
                  </h3>
                </div>

                <label className="form-label">
                  Video Topic / Concept{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., 5 productivity habits that changed my life, iPhone 15 Pro Review, How to start investing..."
                />

                <label className="form-label">
                  Target Audience <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  className="form-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  {audienceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Script Length</label>
                    <select
                      className="form-select"
                      value={wordLength}
                      onChange={(e) => setWordLength(e.target.value)}
                    >
                      {lengthOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Tone of Voice</label>
                    <select
                      className="form-select"
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                    >
                      {toneOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className="form-label">Primary Goal</label>
                <select
                  className="form-select"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                >
                  {goalOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1.25rem",
                    borderRadius: "12px",
                    marginTop: "0.5rem",
                    marginBottom: "1.5rem",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                          display: "block",
                          fontSize: "0.95rem",
                        }}
                      >
                        Include Hooks
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        Generate 2-3 hook variations
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeHooks}
                        onChange={(e) => setIncludeHooks(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontWeight: "600",
                          color: "#1e293b",
                          display: "block",
                          fontSize: "0.95rem",
                        }}
                      >
                        Call to Action (CTA)
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        Include subscribe/watch next CTA
                      </span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={includeCta}
                        onChange={(e) => setIncludeCta(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexDirection: "column",
                  }}
                >
                  <button
                    onClick={generateScript}
                    disabled={generating || !topic.trim() || !audience.trim()}
                    className="gradient-btn"
                  >
                    {generating ? (
                      <Clock size={18} className="animate-spin" />
                    ) : (
                      <Sparkles size={18} />
                    )}
                    {generating ? "Drafting Script..." : `Write Script `}
                  </button>
                  <button onClick={resetForm} className="outline-btn">
                    Clear Form
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Output */}
            <div style={{ flex: "1 1 500px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    color: "#0f172a",
                    fontWeight: "700",
                  }}
                >
                  Generated Script
                </h2>
                {scriptData && (
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={copyToClipboard}
                      style={{
                        background: "#fff",
                        color: "#334155",
                        border: "1px solid #cbd5e1",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                      }}
                    >
                      <Copy size={16} /> Copy
                    </button>
                    <button
                      onClick={downloadContent}
                      style={{
                        background: "#10b981",
                        color: "#fff",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)",
                      }}
                    >
                      <Download size={16} /> Export
                    </button>
                  </div>
                )}
              </div>

              {generating ? (
                <div
                  className="saas-card animate-pulse-custom"
                  style={{
                    textAlign: "center",
                    padding: "5rem 2rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      background: "#fef2f2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.5rem",
                    }}
                  >
                    <FileText size={32} color="#ef4444" />
                  </div>
                  <h3
                    style={{
                      color: "#0f172a",
                      fontWeight: "700",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Writing your script...
                  </h3>
                  <p
                    style={{
                      color: "#64748b",
                      maxWidth: "300px",
                      margin: "0 auto",
                    }}
                  >
                    Structuring hooks, formatting content, and optimizing for
                    audience retention.
                  </p>
                </div>
              ) : scriptData ? (
                <div className="saas-card" style={{ padding: "2rem" }}>
                  <h2
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "800",
                      color: "#0f172a",
                      marginBottom: "2rem",
                      borderBottom: "2px solid #f1f5f9",
                      paddingBottom: "1rem",
                      lineHeight: "1.4",
                    }}
                  >
                    {scriptData.title}
                  </h2>

                  {/* Hooks Block */}
                  {scriptData.hooks && scriptData.hooks.length > 0 && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #ef4444" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#fef2f2", color: "#b91c1c" }}
                      >
                        <Zap size={14} /> The Hook (0:00 - 0:30)
                      </div>
                      {scriptData.hooks.map((hook, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom:
                              idx !== scriptData.hooks.length - 1 ? "1rem" : 0,
                          }}
                        >
                          {scriptData.hooks.length > 1 && (
                            <strong
                              style={{
                                color: "#64748b",
                                fontSize: "0.85rem",
                                display: "block",
                                marginBottom: "4px",
                              }}
                            >
                              OPTION {idx + 1}:
                            </strong>
                          )}
                          <p className="script-text">"{hook}"</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Intro Block */}
                  {scriptData.intro && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #3b82f6" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#eff6ff", color: "#1d4ed8" }}
                      >
                        <PlayCircle size={14} /> Intro & Setup
                      </div>
                      <p className="script-text">{scriptData.intro}</p>
                    </div>
                  )}

                  {/* Main Content Sections */}
                  {scriptData.sections && scriptData.sections.length > 0 && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #8b5cf6" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#f5f3ff", color: "#6d28d9" }}
                      >
                        <AlignLeft size={14} /> Main Content
                      </div>
                      {scriptData.sections.map((section, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom:
                              idx !== scriptData.sections.length - 1
                                ? "2rem"
                                : 0,
                          }}
                        >
                          <h4
                            style={{
                              fontSize: "1.1rem",
                              fontWeight: "700",
                              color: "#334155",
                              marginBottom: "0.75rem",
                            }}
                          >
                            {section.heading}
                          </h4>
                          <p className="script-text">{section.script}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Engagement Prompt */}
                  {scriptData.engagement && (
                    <div
                      className="script-block"
                      style={{
                        borderLeft: "4px solid #f59e0b",
                        background: "#fffbeb",
                      }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#fef3c7", color: "#b45309" }}
                      >
                        <MessageCircle size={14} /> Engagement Prompt
                      </div>
                      <p
                        className="script-text"
                        style={{ fontStyle: "italic" }}
                      >
                        {scriptData.engagement}
                      </p>
                    </div>
                  )}

                  {/* Call To Action */}
                  {scriptData.cta && (
                    <div
                      className="script-block"
                      style={{ borderLeft: "4px solid #10b981" }}
                    >
                      <div
                        className="script-badge"
                        style={{ background: "#ecfdf5", color: "#047857" }}
                      >
                        <Target size={14} /> Call To Action
                      </div>
                      <p className="script-text">{scriptData.cta}</p>
                    </div>
                  )}

                  {/* Outro */}
                  {scriptData.outro && (
                    <div
                      className="script-block"
                      style={{
                        borderLeft: "4px solid #64748b",
                        marginBottom: 0,
                      }}
                    >
                      <div className="script-badge">
                        <CheckCircle size={14} /> Outro & Sign-off
                      </div>
                      <p className="script-text">{scriptData.outro}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className="saas-card"
                  style={{
                    textAlign: "center",
                    padding: "5rem 2rem",
                    border: "2px dashed #cbd5e1",
                    background: "transparent",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <FileText
                    size={48}
                    color="#cbd5e1"
                    style={{ marginBottom: "1.5rem" }}
                  />
                  <h3
                    style={{
                      color: "#64748b",
                      fontWeight: "700",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    Your Script Awaits
                  </h3>
                  <p style={{ color: "#94a3b8", margin: 0, maxWidth: "300px" }}>
                    Fill out the parameters on the left to draft a
                    high-retention YouTube script.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default YoutubeScriptGenerator;
